import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';
import { getValueInSessionStorage } from '../../utils/cacheSession';
import { handleFormDataWithObject } from '../../utils/formData';
// routes
import { PATH_API } from '../../routes/paths';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  claimsAllTime: {},
  claims12Month: {},
  claim: null,
  benefit: null,
  policyList: null,
  groupClaimRules: null,
  submitResponse: null,
};

const slice = createSlice({
  name: 'claim',
  initialState,
  reducers: {
    // * START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // * HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // * GET CLAIMS All time
    getClaimsAllTimeSuccess(state, action) {
      state.isLoading = false;
      state.claimsAllTime = action.payload;
    },

    // * GET CLAIMS 12 MONTHs
    getClaims12MonthsSuccess(state, action) {
      state.isLoading = false;
      state.claims12Month = action.payload;
    },

    // * GET CLAIM ALL TIME
    getClaimSuccess(state, action) {
      state.isLoading = false;
      state.claim = {
        ...state.claims12Month?.detail?.filter((claim) => claim.notifyNo === action.payload)[0],
      };
    },

    // * GET CLAIM BENEFIT
    getClaimBenefitSuccess(state, action) {
      state.isLoading = false;
      state.benefit = action.payload;
    },

    getValidatePolicySuccess(state, action) {
      state.isLoading = false;
      state.policyList = action.payload;
    },

    // * GET VALIDATE POLICY
    getValidateGroupClaimRulesSuccess(state, action) {
      state.isLoading = false;
      state.groupClaimRules = action.payload;
    },
    // * GET VALIDATE POLICY
    submitClaimSuccess(state, action) {
      state.isLoading = false;
      state.submitResponse = action.payload;
    },

    // * CLEAR CLAIM STATE
    clearClaimState(state) {
      Object.assign(state, initialState);
    },
  },
});

// Reducer
export default slice.reducer;

export const { clearClaimState } = slice.actions;

// ----------------------------------------------------------------------

export function getClaimsAllTime(policyNo, certificate, familyNo) {
  return async () => {
    dispatch(slice.actions.startLoading());

    const { nationalID } = getValueInSessionStorage('userInfo');

    try {
      const claimResponse = await axios.post(PATH_API.claim.claimHistoryAllTime, {
        params: { policyNo, certificate, familyNo },
        nationalID,
        policyNo,
        certificate,
        familyNo,
      });

      dispatch(slice.actions.getClaimsAllTimeSuccess(claimResponse.data[0]));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getClaims12Months(policyNo, certificate, familyNo) {
  return async () => {
    dispatch(slice.actions.startLoading());

    const { nationalID } = getValueInSessionStorage('userInfo');

    try {
      const res = await axios.post(PATH_API.claim.claimHistory12Months, {
        nationalID,
        policyNo,
        certificate,
        familyNo,
      });

      dispatch(slice.actions.getClaims12MonthsSuccess(res.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getClaim(notifyNo) {
  return () => {
    dispatch(slice.actions.startLoading());
    dispatch(slice.actions.getClaimSuccess(notifyNo));
  };
}

export function getClaimBenefit(policyNo, certificate, familyNo) {
  return async () => {
    dispatch(slice.actions.startLoading());
    const { nationalID } = getValueInSessionStorage('userInfo');

    try {
      const res = await axios.post(PATH_API.claim.claimBenefitAndBalance, {
        nationalID,
        policyNo,
        certificate,
        familyNo,
      });

      dispatch(slice.actions.getClaimBenefitSuccess(res.data[0]));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getValidatePolicy(nationalID) {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const res = await axios.post(PATH_API.claim.validateRules, {
        nationalID,
      });

      dispatch(slice.actions.getValidatePolicySuccess(res.data.data.policyList));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getValidateGroupClaimRules(policy, nationalID) {
  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      const res = await axios.post(PATH_API.claim.validateGroupClaimRules, {
        policyList: policy,
        nationalID,
      });

      dispatch(slice.actions.getValidateGroupClaimRulesSuccess(res.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

function handleUploadFile(resUploads) {
  const images = {
    bookBankImgs: [],
    medicalCertificateImgs: [],
    receiptImgs: [],
    slipImgs: [],
  };

  const fileMap = {};
  resUploads.uploads.forEach((image) => {
    const key = image.path;

    resUploads.len += image.size;

    if (!fileMap[key]) {
      images[image.fieldname].push(key);
      fileMap[key] = true;
    }
  });

  return images;
}

// Removed submitClaim function from here (moved to new version above)

export function submitClaim(body, nationalID) {
  const chunkSize = 1024 * 1024;
  const allUploads = {
    receiptImgs: [],
    bookBankImgs: [],
    slipImgs: [],
    medicalCertificateImgs: [],
  };

  const { receiptImgs, bookBankImgs, slipImgs, medicalCertificateImgs, attachedDocLen, ...other } =
    body;

  // Upload file one at a time without chunking (keep it simple and reliable)
  const uploadFile = async (file, fieldName, fileIndex) => {
    const formData = new FormData();
    
    // Add file metadata
    handleFormDataWithObject({ nationalID, attachedDocLen: attachedDocLen.curr }, formData);
    
    // Add the file itself
    const fileName = `${fieldName}_${fileIndex}.${file.type.split('/')[1]}`;
    formData.append(fieldName, file, fileName);

    try {
      const res = await axios.post(PATH_API.claim.uploads, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minute timeout per file
        onUploadProgress: (progressEvent) => {
          // Calculate accurate progress for this single file
          const progress = progressEvent.loaded / progressEvent.total;
          console.log(`Uploading ${fieldName}[${fileIndex}]: ${Math.round(progress * 100)}%`);
        },
      });

      if (res.status === 200 && res.data) {
        // Store upload results by fieldName
        res.data.forEach((uploadItem) => {
          if (uploadItem.fieldname && allUploads[uploadItem.fieldname]) {
            allUploads[uploadItem.fieldname].push(uploadItem.path);
          }
        });
      }

      return res.data;
    } catch (error) {
      console.error(`Error uploading file ${fieldName}[${fileIndex}]:`, error);
      throw error;
    }
  };

  // Process all files sequentially to avoid resource exhaustion
  const uploadAllFiles = async () => {
    const uploadPromises = [];

    // Queue all upload tasks
    if (receiptImgs?.length > 0) {
      receiptImgs.forEach((file, index) => {
        uploadPromises.push(uploadFile(file, 'receiptImgs', index));
      });
    }

    if (bookBankImgs?.length > 0) {
      bookBankImgs.forEach((file, index) => {
        uploadPromises.push(uploadFile(file, 'bookBankImgs', index));
      });
    }

    if (slipImgs?.length > 0) {
      slipImgs.forEach((file, index) => {
        uploadPromises.push(uploadFile(file, 'slipImgs', index));
      });
    }

    if (medicalCertificateImgs?.length > 0) {
      medicalCertificateImgs.forEach((file, index) => {
        uploadPromises.push(uploadFile(file, 'medicalCertificateImgs', index));
      });
    }

    // Wait for all uploads to complete
    if (uploadPromises.length > 0) {
      await Promise.all(uploadPromises);
    }
  };

  return async () => {
    dispatch(slice.actions.startLoading());

    try {
      // Upload all images first
      await uploadAllFiles();

      // Prepare final submission data
      const data = { 
        ...other,
        receiptImgs: allUploads.receiptImgs,
        bookBankImgs: allUploads.bookBankImgs,
        slipImgs: allUploads.slipImgs,
        medicalCertificateImgs: allUploads.medicalCertificateImgs,
      };

      // Submit claim with uploaded file paths (with timeout)
      const response = await axios.post(PATH_API.claim.claimSubmit, data, {
        timeout: 30000, // 30 second timeout for final submission
      });

      dispatch(slice.actions.submitClaimSuccess(response.data));
      return response.data;
    } catch (error) {
      console.error('Error during claim submission:', error);
      dispatch(slice.actions.hasError(error));
      // Return error response to prevent infinite loading
      dispatch(slice.actions.submitClaimSuccess({ code: '500', message: error.message }));
      return null;
    }
  };
}